import { useMemo, useCallback } from 'react'
import { cn } from '@/lib/utils'

interface DateRangeSelectorProps {
  startDate: string
  endDate: string
  onStartDateChange: (date: string) => void
  onEndDateChange: (date: string) => void
  className?: string
  disabled?: boolean
  compact?: boolean
}

type PresetKey = '1Y' | '3Y' | '5Y' | '10Y' | 'Max'

interface Preset {
  label: PresetKey
  yearsBack: number | null // null for Max
}

const PRESETS: Preset[] = [
  { label: '1Y', yearsBack: 1 },
  { label: '3Y', yearsBack: 3 },
  { label: '5Y', yearsBack: 5 },
  { label: '10Y', yearsBack: 10 },
  { label: 'Max', yearsBack: null },
]

/**
 * Formats a Date object to YYYY-MM-DD string
 */
function formatDateToISO(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Calculate date N years ago from today
 */
function getDateYearsAgo(years: number): string {
  const date = new Date()
  date.setFullYear(date.getFullYear() - years)
  return formatDateToISO(date)
}

/**
 * Get today's date in ISO format
 */
function getTodayISO(): string {
  return formatDateToISO(new Date())
}

/**
 * Date range picker with preset buttons
 * Allows selection of custom date range or quick presets (1Y, 3Y, 5Y, 10Y, Max)
 */
export function DateRangeSelector({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  className,
  disabled = false,
  compact = false,
}: DateRangeSelectorProps) {
  const today = useMemo(() => getTodayISO(), [])
  const maxStartDate = '1970-01-01'

  // Determine which preset is currently active (if any)
  const activePreset = useMemo((): PresetKey | null => {
    const todayDate = getTodayISO()

    // End date should be today for presets to be active
    if (endDate !== todayDate) {
      return null
    }

    for (const preset of PRESETS) {
      if (preset.yearsBack === null) {
        // Max preset
        if (startDate === maxStartDate) {
          return 'Max'
        }
      } else {
        const expectedStart = getDateYearsAgo(preset.yearsBack)
        if (startDate === expectedStart) {
          return preset.label
        }
      }
    }

    return null
  }, [startDate, endDate])

  // Handle preset button click
  const handlePresetClick = useCallback(
    (preset: Preset) => {
      const todayDate = getTodayISO()
      onEndDateChange(todayDate)

      if (preset.yearsBack === null) {
        // Max preset - set to 1970-01-01
        onStartDateChange(maxStartDate)
      } else {
        onStartDateChange(getDateYearsAgo(preset.yearsBack))
      }
    },
    [onStartDateChange, onEndDateChange]
  )

  // Validate date range
  const isValidRange = useMemo(() => {
    if (!startDate || !endDate) return true
    return new Date(startDate) <= new Date(endDate)
  }, [startDate, endDate])

  // Compact mode: Inline date inputs without preset buttons
  if (compact) {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        <input
          id="start-date-compact"
          type="date"
          value={startDate}
          onChange={(e) => onStartDateChange(e.target.value)}
          min={maxStartDate}
          max={endDate || today}
          disabled={disabled}
          placeholder="Start date"
          title="Start date"
          className={cn(
            'w-[130px] py-1.5 px-2 text-sm border rounded-lg',
            'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
            'disabled:bg-gray-100 disabled:cursor-not-allowed dark:disabled:bg-gray-800',
            'dark:bg-gray-900 dark:border-gray-700 dark:text-white',
            '[color-scheme:dark]',
            !isValidRange && 'border-red-500 focus:ring-red-500 focus:border-red-500'
          )}
          aria-label="Start date"
        />
        <span className="text-gray-400 text-sm">-</span>
        <input
          id="end-date-compact"
          type="date"
          value={endDate}
          onChange={(e) => onEndDateChange(e.target.value || getTodayISO())}
          min={startDate || maxStartDate}
          max={today}
          disabled={disabled}
          placeholder="End date"
          title="End date"
          className={cn(
            'w-[130px] py-1.5 px-2 text-sm border rounded-lg',
            'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
            'disabled:bg-gray-100 disabled:cursor-not-allowed dark:disabled:bg-gray-800',
            'dark:bg-gray-900 dark:border-gray-700 dark:text-white',
            '[color-scheme:dark]',
            !isValidRange && 'border-red-500 focus:ring-red-500 focus:border-red-500'
          )}
          aria-label="End date"
        />
      </div>
    )
  }

  // Default (non-compact) mode
  return (
    <div className={cn('w-full', className)}>
      <fieldset disabled={disabled}>
        <legend className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Date Range
        </legend>

        {/* Preset buttons */}
        <div
          className="flex flex-wrap gap-2 mb-4"
          role="group"
          aria-label="Date range presets"
        >
          {PRESETS.map((preset) => (
            <button
              key={preset.label}
              type="button"
              onClick={() => handlePresetClick(preset)}
              disabled={disabled}
              className={cn(
                'px-4 py-2 text-sm font-medium rounded-lg',
                'border transition-colors duration-200',
                'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                activePreset === preset.label
                  ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700'
              )}
              aria-pressed={activePreset === preset.label}
            >
              {preset.label}
            </button>
          ))}
        </div>

        {/* Date inputs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Start date */}
          <div>
            <label
              htmlFor="start-date"
              className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1"
            >
              Start Date
            </label>
            <input
              id="start-date"
              type="date"
              value={startDate}
              onChange={(e) => onStartDateChange(e.target.value)}
              min={maxStartDate}
              max={endDate || today}
              disabled={disabled}
              className={cn(
                'w-full px-4 py-2 border rounded-lg',
                'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                'disabled:bg-gray-100 disabled:cursor-not-allowed dark:disabled:bg-gray-800',
                'dark:bg-gray-900 dark:border-gray-700 dark:text-white',
                '[color-scheme:dark]',
                !isValidRange && 'border-red-500 focus:ring-red-500 focus:border-red-500'
              )}
              aria-describedby={!isValidRange ? 'date-range-error' : undefined}
            />
          </div>

          {/* End date */}
          <div>
            <label
              htmlFor="end-date"
              className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1"
            >
              End Date
            </label>
            <input
              id="end-date"
              type="date"
              value={endDate}
              onChange={(e) => onEndDateChange(e.target.value || getTodayISO())}
              min={startDate || maxStartDate}
              max={today}
              disabled={disabled}
              className={cn(
                'w-full px-4 py-2 border rounded-lg',
                'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                'disabled:bg-gray-100 disabled:cursor-not-allowed dark:disabled:bg-gray-800',
                'dark:bg-gray-900 dark:border-gray-700 dark:text-white',
                '[color-scheme:dark]',
                !isValidRange && 'border-red-500 focus:ring-red-500 focus:border-red-500'
              )}
              aria-describedby={!isValidRange ? 'date-range-error' : undefined}
            />
          </div>
        </div>

        {/* Error message */}
        {!isValidRange && (
          <p
            id="date-range-error"
            className="mt-2 text-sm text-red-600 dark:text-red-400"
            role="alert"
          >
            Start date must be before or equal to end date.
          </p>
        )}

        {/* Helper text */}
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          Select a preset or choose custom dates. Historical data availability depends on the ticker.
        </p>
      </fieldset>
    </div>
  )
}

export default DateRangeSelector
